import ParameterValidator from '@mvps-genesis/parameter-validator';
import {getAbsoluteParent, getNextSibling, getParent} from '@mvps-genesis/selector-helpers';
import {createFocusTrap} from 'focus-trap';

/* eslint-disable no-invalid-this */
export default class NavigationMenu {

	/**
	 * Options:
	 *
	 * options.drawers
	 * options.mainMenu
	 * options.mainMenuOpenButtons
	 * options.mainMenuCloseButtons
	 * options.menuItems
	 * options.parentMenus
	 * options.showPreviousMenuButtons
	 */
	constructor (options) {

		// Create validator
		this._validator = new ParameterValidator(this);

		this.options = this._validator.isObject('options', options);
		this.breakpoint = this._validator.isString('options.breakpoint', options.breakpoint || '75em');
		this.options.allParentMenus = this._validator.isString('options.allParentMenus', options.allParentMenus || '');
		this.options.mobileHeader = this._validator.isString('options.mobileHeader', options.mobileHeader || '');
		this.options.mobileFooter = this._validator.isString('options.mobileFooter', options.mobileFooter || '');

		this.elements = {
			mainMenu: this._validator.isElement('options.mainMenu', options.mainMenu),
			mainMenuCloseButtons: this._validator.isElementList('options.mainMenuCloseButtons', options.mainMenuCloseButtons),
			mainMenuOpenButtons: this._validator.isElementList('options.mainMenuOpenButtons', options.mainMenuOpenButtons),
			parentMenus: this._validator.isElementList('options.parentMenus', options.parentMenus),
			showPreviousMenuButtons: this._validator.isElementList('options.showPreviousMenuButtons', options.showPreviousMenuButtons)
		};
		this.elements.menuItems = this._validator.isElementList('options.menuItems', options.menuItems, {container: this.elements.mainMenu});
		this.elements.parentMenuItems = this._validator.isElementList('parentMenuItems', `${options.parentMenus} > ${options.menuItems}`, {container: this.elements.mainMenu});

		this.accessibility = {
			// Create focus trap for mobile menu
			focusTrap: createFocusTrap(this.elements.mainMenu, {
				escapeDeactivates: false,
				setReturnFocus: this.elements.mainMenuOpenButtons[0]
			})
		};

		this.bindEvents();
	}

	isMobile () {
		return this.breakpoint ? window.matchMedia(`(max-width: ${this.breakpoint})`).matches : false;
	}

	bindEvents (add = true) {
		const method = add ? 'addEventListener' : 'removeEventListener';

		// Mobile open and close events
		for (const mainMenuOpenButton of this.elements.mainMenuOpenButtons) {
			mainMenuOpenButton[method]('click', this.mainMenuOpen, false);
		}
		for (const mainMenuCloseButton of this.elements.mainMenuCloseButtons) {
			mainMenuCloseButton[method]('click', this.mainMenuClose, false);
		}

		for (const menuItem of this.elements.menuItems) {
			menuItem[method]('blur', this.onMenuItemBlur, false);
			menuItem[method]('focus', this.onMenuItemFocus, false);
		}

		for (const parentMenuItem of this.elements.parentMenuItems) {
			parentMenuItem[method]('click', this.onParentMenuItemClick, false);
			parentMenuItem[method]('keydown', this.onParentMenuItemKeydown, false);
		}

		// Show previous button
		for (const showPreviousMenuButton of this.elements.showPreviousMenuButtons) {
			showPreviousMenuButton[method]('click', this.onPreviousButtonClick, false);
		}

		this.elements.mainMenu[method]('keydown', this.onMenuKeydown, false);
	}

	destroyEvents () {
		this.bindEvents(false);
	}

	setChildTabIndex = (container, value) => {
		if (!container) {
			return;
		}

		const childItems = container.querySelectorAll(this.options.menuItems);

		for (const item of childItems) {
			item.setAttribute('tabindex', value);
		}

		const backButtons = container.querySelectorAll(this.options.showPreviousMenuButtons);

		for (const button of backButtons) {
			button.setAttribute('tabindex', value);
		}
	}

	setRootItemsTabIndex = (value) => {
		if (this.options.mobileHeader) {
			const headerLinks = this.elements.mainMenu.querySelectorAll(`${this.options.mobileHeader} ${this.options.menuItems}`);

			for (const link of headerLinks) {
				link.setAttribute('tabindex', value);
			}
		}

		if (this.options.allParentMenus) {
			const parentTriggers = this.elements.mainMenu.querySelectorAll(`${this.options.allParentMenus} > ${this.options.menuItems}`);

			for (const trigger of parentTriggers) {
				trigger.setAttribute('tabindex', value);
			}
		}

		if (this.options.mobileFooter) {
			const footerLinks = this.elements.mainMenu.querySelectorAll(`${this.options.mobileFooter} ${this.options.menuItems}`);

			for (const link of footerLinks) {
				link.setAttribute('tabindex', value);
			}
		}
	}

	mainMenuOpen = () => {
		document.documentElement.classList.add('has-open-main-menu');
		this.elements.mainMenu.classList.add('is-open');
		this.elements.mainMenu.classList.remove('is-hidden');

		if (this.isMobile()) {
			this.setRootItemsTabIndex('0');
		}

		const firstItem = this.elements.mainMenu.querySelector(this.options.menuItems);

		if (firstItem) {
			firstItem.focus();
		}

		this.accessibility.focusTrap.activate();
	}

	mainMenuClose = () => {
		document.documentElement.classList.remove('has-open-main-menu');
		this.elements.mainMenu.classList.remove('is-open');
		this.elements.mainMenu.classList.add('is-hidden');

		this.closeAllDrawers();

		if (this.isMobile()) {
			this.setRootItemsTabIndex('-1');
		}

		this.accessibility.focusTrap.deactivate();
	}

	closeAllDrawers = () => {
		const openParents = this.elements.mainMenu.querySelectorAll(`${this.options.parentMenus}.is-open`);

		for (const parent of openParents) {
			parent.classList.remove('is-open');
		}

		const openDrawers = this.elements.mainMenu.querySelectorAll(`${this.options.drawers}.is-open`);

		for (const drawer of openDrawers) {
			this.setChildTabIndex(drawer, '-1');
			drawer.classList.remove('is-open');
		}

		const expandedItems = this.elements.mainMenu.querySelectorAll('[aria-expanded="true"]');

		for (const item of expandedItems) {
			item.setAttribute('aria-expanded', 'false');
		}
	}

	openDrawer = (parentElement) => {
		if (!parentElement) {
			return;
		}

		parentElement.classList.add('is-open');

		const trigger = parentElement.querySelector(this.options.menuItems);

		if (trigger && trigger.getAttribute('aria-expanded') !== null) {
			trigger.setAttribute('aria-expanded', 'true');
		}

		const drawer = parentElement.querySelector(this.options.drawers);

		this.setChildTabIndex(drawer, '0');
	}

	closeDrawer = (parentElement) => {
		if (!parentElement) {
			return;
		}

		parentElement.classList.remove('is-open');

		const openDrawers = parentElement.querySelectorAll(`${this.options.drawers}.is-open`);

		for (const drawer of openDrawers) {
			drawer.classList.remove('is-open');
		}


		const allDrawers = parentElement.querySelectorAll(this.options.drawers);

		for (const drawer of allDrawers) {
			this.setChildTabIndex(drawer, '-1');
		}

		const trigger = parentElement.querySelector(this.options.menuItems);

		if (trigger && trigger.getAttribute('aria-expanded') !== null) {
			trigger.setAttribute('aria-expanded', 'false');
		}
	}

	onMenuItemFocus = (e) => {
		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			this.openDrawer(parent);
		}
	}

	onMenuItemBlur = (e) => {
		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			requestAnimationFrame(() => {
				const newFocusedElement = document.activeElement;
				const newParent = getAbsoluteParent(newFocusedElement, this.options.parentMenus);

				if (newParent !== parent) {
					this.closeDrawer(parent);
				}
			});
		}
	}

	onParentMenuItemKeydown = (e) => {
		if (this.isMobile()) {
			return;
		}

		if (e.key === 'Enter' || e.key === ' ') {
			const parent = getAbsoluteParent(e.target, this.options.parentMenus);

			if (!parent) {
				return;
			}

			const drawer = parent.querySelector(this.options.drawers);

			if (!drawer) {
				return;
			}

			e.preventDefault();

			this.openDrawer(parent);

			const firstDrawerLink = drawer.querySelector(this.options.menuItems);

			if (firstDrawerLink) {
				firstDrawerLink.focus();
			}
		}
	}

	onMenuKeydown = (e) => {
		if (e.key !== 'Escape') {
			return;
		}

		if (this.isMobile()) {
			const openDrawers = this.elements.mainMenu.querySelectorAll(`${this.options.drawers}.is-open`);

			if (openDrawers.length > 0) {
				const innermostDrawer = openDrawers[openDrawers.length - 1];

				e.preventDefault();
				e.stopPropagation();

				this.setChildTabIndex(innermostDrawer, '-1');
				innermostDrawer.classList.remove('is-open');

				const remainingOpenDrawers = this.elements.mainMenu.querySelectorAll(`${this.options.drawers}.is-open`);

				if (remainingOpenDrawers.length === 0) {
					this.setRootItemsTabIndex('0');
				}
			} else {
				this.mainMenuClose();
			}

			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent && parent.classList.contains('is-open')) {
			e.preventDefault();
			e.stopPropagation();

			this.closeDrawer(parent);

			const trigger = parent.querySelector(`:scope > ${this.options.menuItems}`);

			if (trigger) {
				trigger.focus();
			}
		}
	}

	onParentMenuItemClick = (e) => {
		e.stopPropagation();

		if (!this.isMobile()) {
			return true;
		}

		e.preventDefault();

		let clickElement = e.target;
		const parentElement = getParent(clickElement, this.options.menuItems);

		if (parentElement) {
			clickElement = parentElement;
		}

		const nextDrawer = getNextSibling(clickElement, this.options.drawers);

		if (nextDrawer) {
			this.setRootItemsTabIndex('-1');
			this.setChildTabIndex(nextDrawer, '0');
			nextDrawer.classList.add('is-open');
		}

		return false;
	}

	onPreviousButtonClick = (e) => {
		e.stopPropagation();
		e.preventDefault();

		const parentDrawer = getParent(e.target, this.options.drawers);

		if (parentDrawer) {
			this.setChildTabIndex(parentDrawer, '-1');
			parentDrawer.classList.remove('is-open');

			const remainingOpenDrawers = this.elements.mainMenu.querySelectorAll(`${this.options.drawers}.is-open`);

			if (remainingOpenDrawers.length === 0) {
				this.setRootItemsTabIndex('0');
			}
		}
	}

	showPreviousMenu = (target) => {
		const parentDrawer = getParent(target, this.options.drawers);

		if (parentDrawer) {
			this.setChildTabIndex(parentDrawer, '-1');
			parentDrawer.classList.remove('is-open');

			const remainingOpenDrawers = this.elements.mainMenu.querySelectorAll(`${this.options.drawers}.is-open`);

			if (remainingOpenDrawers.length === 0 && this.isMobile()) {
				this.setRootItemsTabIndex('0');
			}
		}
	}
}